import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import type { ChatReplyTo } from '@hrms/shared';
import { tokens, tsr } from './api.js';

export interface RealtimeNotification {
  id: string;
  key: string;
  title: string;
  body?: string;
  href?: string;
  createdAt: string;
  /** Small whitelisted passthrough for consumers that need one raw field beyond the rendered
   * title/body — see `packages/notify/src/realtime.ts`'s `RealtimeNotification.data` doc comment.
   * Today only `taskId`, populated for the task-reminder "remind me later" snooze flush. */
  data?: Record<string, string | number | boolean>;
}

export interface RealtimeMessage {
  id: string;
  conversationId: string;
  seq: string;
  senderId?: string | null;
  /** Echoed back from the server so the sender can reconcile optimistic messages. */
  clientMsgId?: string | null;
  kind: 'text' | 'file' | 'system' | 'ai' | 'approval_card';
  body?: string | null;
  parentId?: string | null;
  /** The quoted original when this is a reply (see `chatReplyToSchema` in the shared contract). */
  replyTo?: ChatReplyTo | null;
  meta?: Record<string, unknown> | null;
  editedAt?: string | null;
  deletedAt?: string | null;
  attachments?: Array<{ id: string; fileName: string; mimeType: string; sizeBytes: number }>;
  createdAt: string;
}

type NotificationListener = (payload: RealtimeNotification) => void;
type MessageListener = (msg: RealtimeMessage) => void;
/** Fired for both an edit and a delete — see the server's `message:updated` doc comment. */
type MessageUpdateListener = (msg: RealtimeMessage) => void;
export interface ConversationMuteChange {
  conversationId: string;
  mutedUntil: string | null;
}
/** Fired on this person's OTHER connections when they mute/unmute a conversation from one of them. */
type ConversationMuteListener = (change: ConversationMuteChange) => void;
/** Fired when THIS person was removed from (or left, on another device) a conversation. The
 * gateway has already evicted every one of their sockets from its room. */
type ConversationMemberRemovedListener = (payload: { conversationId: string }) => void;
/** "Delete for me" from any of this person's devices: drop the message from their view. Sent only
 * to the person who hid it — the message still exists for everyone else. */
export interface MessageHiddenEvent {
  conversationId: string;
  messageId: string;
}
type MessageHiddenListener = (event: MessageHiddenEvent) => void;
export interface ReactionChange {
  convId: string;
  messageId: string;
  emoji: string;
  personId: string;
  active: boolean;
  changed: boolean;
}
type ReactionListener = (change: ReactionChange) => void;
type ReadListener = (data: { convId: string; personId: string; lastReadSeq: string }) => void;
export interface TypingEvent {
  convId: string;
  personId: string;
  typing: boolean;
}
type TypingListener = (event: TypingEvent) => void;

interface SocketAck<T> {
  ok?: boolean;
  data?: T;
  error?: { message?: string };
}

/** What the server confirmed for a sent message. `body`/`meta` are the server's copy — which may
 * differ from what was typed (DLP redaction, server-set mention metadata) and is what the sender
 * must display from then on. */
export interface SentMessageAck {
  id: string;
  seq: string;
  createdAt: string;
  body?: string | null;
  meta?: Record<string, unknown> | null;
}

/** How long a `message:send` may wait for its ack before it is treated as failed. The send is
 * idempotent on `clientMsgId`, so a retry after a timeout can never produce a second message. */
export const SEND_ACK_TIMEOUT_MS = 15_000;

/** How long a `message:read` may wait for its ack before `markRead` falls back to the REST route. */
export const READ_ACK_TIMEOUT_MS = 4_000;

/**
 * How often an open socket re-presents a fresh access token (`auth:refresh`). The gateway closes a
 * connection whose re-auth deadline (connect/refresh time + the 15-minute access-token TTL, see
 * apps/ws/src/auth.ts) passes; refreshing every 10 minutes keeps a comfortable margin even when a
 * background tab's timers are throttled.
 */
const SOCKET_REAUTH_INTERVAL_MS = 10 * 60 * 1000;
/** Retry delay after a re-auth attempt failed for a transient reason (network blip). */
const SOCKET_REAUTH_RETRY_MS = 60 * 1000;

type ReconnectListener = () => void;

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  /** Fires on every successful connect AFTER the first one — i.e. after a reconnect, when anything
   * delivered while the socket was down was missed and must be refetched. */
  onReconnect(listener: ReconnectListener): () => void;
  onNotification(listener: NotificationListener): () => void;
  onMessage(listener: MessageListener): () => void;
  onMessageUpdated(listener: MessageUpdateListener): () => void;
  onMessageHidden(listener: MessageHiddenListener): () => void;
  onConversationMuted(listener: ConversationMuteListener): () => void;
  onConversationMemberRemoved(listener: ConversationMemberRemovedListener): () => void;
  onRead(listener: ReadListener): () => void;
  onReactionChanged(listener: ReactionListener): () => void;
  /** Ask the gateway to broadcast a file message the REST upload route already created. */
  announceMessage(payload: { convId: string; messageId: string }): Promise<void>;
  setReaction(payload: {
    convId: string;
    messageId: string;
    emoji: string;
    active: boolean;
  }): Promise<ReactionChange>;
  joinConversation(convId: string): void;
  leaveConversation(convId: string): void;
  sendMessage(payload: {
    convId: string;
    clientMsgId: string;
    body?: string;
    kind?: 'text' | 'file';
    parentId?: string;
    meta?: Record<string, unknown>;
    /** Person ids the composer's @-autocomplete resolved — the server re-validates every one
     * against live conversation membership before notifying anyone. */
    mentions?: string[];
  }): Promise<SentMessageAck>;
  /** Resolves once the server has recorded the read position (so callers can refresh badges). */
  markRead(convId: string, seq: string | number): Promise<void>;
  onTyping(listener: TypingListener): () => void;
  startTyping(convId: string): void;
  stopTyping(convId: string): void;
  editMessage(payload: {
    convId: string;
    messageId: string;
    body: string;
  }): Promise<RealtimeMessage>;
  deleteMessage(payload: { convId: string; messageId: string }): Promise<RealtimeMessage>;
}

const SocketContext = createContext<SocketContextValue | null>(null);

const SOCKET_PATH = '/ws/socket.io';
const SOCKET_URL: string | undefined = import.meta.env.VITE_WS_URL || undefined;

export function SocketProvider({ children }: { children: ReactNode }) {
  const notifListenersRef = useRef(new Set<NotificationListener>());
  const msgListenersRef = useRef(new Set<MessageListener>());
  const msgUpdateListenersRef = useRef(new Set<MessageUpdateListener>());
  const msgHiddenListenersRef = useRef(new Set<MessageHiddenListener>());
  const conversationMutedListenersRef = useRef(new Set<ConversationMuteListener>());
  const memberRemovedListenersRef = useRef(new Set<ConversationMemberRemovedListener>());
  const readListenersRef = useRef(new Set<ReadListener>());
  const reactionListenersRef = useRef(new Set<ReactionListener>());
  const typingListenersRef = useRef(new Set<TypingListener>());
  const reconnectListenersRef = useRef(new Set<ReconnectListener>());
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    let torndown = false;
    let hasConnectedBefore = false;
    let reauthTimer: ReturnType<typeof setTimeout> | null = null;

    // `auth` is a callback, so EVERY connect attempt — the first, socket.io's own automatic
    // reconnects, and the manual ones below — reads whatever access token is current at that
    // moment, never the one captured when the provider mounted.
    const socket: Socket = io(SOCKET_URL ?? '/', {
      path: SOCKET_PATH,
      auth: (cb) => cb({ token: tokens.getAccessToken() ?? undefined }),
    });
    socketRef.current = socket;

    const clearReauthTimer = () => {
      if (reauthTimer) clearTimeout(reauthTimer);
      reauthTimer = null;
    };
    const scheduleReauth = (delayMs: number) => {
      clearReauthTimer();
      if (torndown) return;
      reauthTimer = setTimeout(() => void refreshSocketAuth(), delayMs);
    };

    /** Makes sure the token store holds a usable access token. `auth.me` goes through the typed
     * client, whose fetcher refreshes on a 401 (under the cross-tab lock) — so an expired access
     * token is rotated here instead of the socket presenting a dead one. Returns false when the
     * session is over (the fetcher has already cleared the store and the app routes to login). */
    async function ensureFreshAccessToken(): Promise<boolean> {
      try {
        const res = await tsr.auth.me.query();
        if (res.status !== 200) return false;
      } catch {
        return false;
      }
      return !!tokens.getAccessToken();
    }

    async function reconnectWithFreshToken() {
      clearReauthTimer();
      socket.disconnect();
      if (!(await ensureFreshAccessToken())) return;
      if (torndown) return;
      socket.connect();
    }

    /** Re-authenticates the OPEN socket before the gateway's re-auth deadline closes it. */
    async function refreshSocketAuth() {
      if (torndown || !socket.connected) return;
      if (!(await ensureFreshAccessToken())) {
        // Either the session is gone (store cleared → stop) or the API was unreachable (retry soon;
        // the deadline is still minutes away).
        if (tokens.getAccessToken()) scheduleReauth(SOCKET_REAUTH_RETRY_MS);
        else socket.disconnect();
        return;
      }
      const token = tokens.getAccessToken();
      if (torndown || !token || !socket.connected) return;

      const outcome = await new Promise<'ok' | 'rejected' | 'timeout'>((resolve) => {
        const timer = setTimeout(() => resolve('timeout'), SEND_ACK_TIMEOUT_MS);
        socket.emit('auth:refresh', token, (response: SocketAck<{ reauthDeadline: number }>) => {
          clearTimeout(timer);
          resolve(response?.ok ? 'ok' : 'rejected');
        });
      });
      if (torndown) return;
      if (outcome === 'ok') {
        scheduleReauth(SOCKET_REAUTH_INTERVAL_MS);
      } else if (outcome === 'timeout') {
        scheduleReauth(SOCKET_REAUTH_RETRY_MS);
      } else {
        // The gateway refused the token for THIS connection (a different session, or revoked).
        // A fresh connection re-runs the full handshake, which refuses a revoked session cleanly.
        void reconnectWithFreshToken();
      }
    }

    function onConnect() {
      setIsConnected(true);
      scheduleReauth(SOCKET_REAUTH_INTERVAL_MS);
      if (hasConnectedBefore) {
        for (const listener of reconnectListenersRef.current) listener();
      }
      hasConnectedBefore = true;
    }

    function onDisconnect() {
      setIsConnected(false);
      clearReauthTimer();
    }

    function onConnectError(err: Error) {
      const code = (err as Error & { data?: { code?: string } }).data?.code;
      if (!code) return;
      if (code === 'token_expired') {
        void reconnectWithFreshToken();
        return;
      }
      console.warn('[socket] connection halted after connect_error', code);
      socket.disconnect();
    }

    function onSessionClosed(payload: { reason: 'revoked' | 'reauth_required' }) {
      console.warn('[socket] session closed', payload.reason);
      clearReauthTimer();
      if (payload.reason === 'reauth_required') {
        // The re-auth deadline passed (a throttled background tab, a sleeping laptop). The session
        // itself may be perfectly alive: fetch a fresh token and open a new connection.
        void reconnectWithFreshToken();
        return;
      }
      // Revoked (logout elsewhere, password change, termination): stop for good. One API call lets
      // the typed client discover the dead session, clear the store, and route to sign-in.
      socket.disconnect();
      void tsr.auth.me.query().catch(() => undefined);
    }

    function onNotificationNew(payload: RealtimeNotification) {
      for (const listener of notifListenersRef.current) listener(payload);
    }

    function onMsgNew(payload: RealtimeMessage) {
      for (const listener of msgListenersRef.current) listener(payload);
    }

    function onReactionChanged(payload: ReactionChange) {
      for (const listener of reactionListenersRef.current) listener(payload);
    }

    function onMsgUpdated(payload: RealtimeMessage) {
      for (const listener of msgUpdateListenersRef.current) listener(payload);
    }

    function onMsgHidden(payload: MessageHiddenEvent) {
      if (!payload?.conversationId || !payload.messageId) return;
      for (const listener of msgHiddenListenersRef.current) listener(payload);
    }

    function onConversationMuted(payload: ConversationMuteChange) {
      for (const listener of conversationMutedListenersRef.current) listener(payload);
    }

    function onMemberRemoved(payload: { conversationId: string }) {
      if (!payload?.conversationId) return;
      for (const listener of memberRemovedListenersRef.current) listener(payload);
    }

    function onConvRead(payload: { convId: string; personId: string; lastReadSeq: string }) {
      for (const listener of readListenersRef.current) listener(payload);
    }

    function onTypingEvent(payload: TypingEvent) {
      for (const listener of typingListenersRef.current) listener(payload);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('session:closed', onSessionClosed);
    socket.on('notification:new', onNotificationNew);
    socket.on('message:new', onMsgNew);
    socket.on('message:updated', onMsgUpdated);
    socket.on('message:hidden', onMsgHidden);
    socket.on('conversation:muted', onConversationMuted);
    socket.on('conversation:member_removed', onMemberRemoved);
    socket.on('reaction:changed', onReactionChanged);
    socket.on('message:read', onConvRead);
    socket.on('typing', onTypingEvent);

    return () => {
      torndown = true;
      clearReauthTimer();
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('session:closed', onSessionClosed);
      socket.off('notification:new', onNotificationNew);
      socket.off('message:new', onMsgNew);
      socket.off('message:updated', onMsgUpdated);
      socket.off('message:hidden', onMsgHidden);
      socket.off('conversation:muted', onConversationMuted);
      socket.off('conversation:member_removed', onMemberRemoved);
      socket.off('reaction:changed', onReactionChanged);
      socket.off('message:read', onConvRead);
      socket.off('typing', onTypingEvent);
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const value = useMemo<SocketContextValue>(
    () => ({
      socket: socketRef.current,
      isConnected,
      onReconnect: (listener) => {
        reconnectListenersRef.current.add(listener);
        return () => reconnectListenersRef.current.delete(listener);
      },
      onNotification: (listener) => {
        notifListenersRef.current.add(listener);
        return () => notifListenersRef.current.delete(listener);
      },
      onMessage: (listener) => {
        msgListenersRef.current.add(listener);
        return () => msgListenersRef.current.delete(listener);
      },
      onMessageUpdated: (listener) => {
        msgUpdateListenersRef.current.add(listener);
        return () => msgUpdateListenersRef.current.delete(listener);
      },
      onMessageHidden: (listener) => {
        msgHiddenListenersRef.current.add(listener);
        return () => msgHiddenListenersRef.current.delete(listener);
      },
      onConversationMuted: (listener) => {
        conversationMutedListenersRef.current.add(listener);
        return () => conversationMutedListenersRef.current.delete(listener);
      },
      onConversationMemberRemoved: (listener) => {
        memberRemovedListenersRef.current.add(listener);
        return () => memberRemovedListenersRef.current.delete(listener);
      },
      onRead: (listener) => {
        readListenersRef.current.add(listener);
        return () => readListenersRef.current.delete(listener);
      },
      onReactionChanged: (listener) => {
        reactionListenersRef.current.add(listener);
        return () => reactionListenersRef.current.delete(listener);
      },
      announceMessage: (payload) => {
        return new Promise((resolve, reject) => {
          if (!socketRef.current) {
            reject(new Error('Socket not connected'));
            return;
          }
          socketRef.current.emit('message:announce', payload, (response: SocketAck<unknown>) => {
            if (response?.ok) resolve();
            else reject(new Error(response?.error?.message || 'Failed to announce message'));
          });
        });
      },
      setReaction: (payload) => {
        return new Promise((resolve, reject) => {
          if (!socketRef.current) {
            reject(new Error('Socket not connected'));
            return;
          }
          socketRef.current.emit('reaction:set', payload, (response: SocketAck<ReactionChange>) => {
            if (response?.ok && response.data) resolve(response.data);
            else reject(new Error(response?.error?.message || 'Failed to update reaction'));
          });
        });
      },
      joinConversation: (convId: string) => {
        socketRef.current?.emit('conversation:join', convId);
      },
      leaveConversation: (convId: string) => {
        socketRef.current?.emit('conversation:leave', convId);
      },
      sendMessage: (payload) => {
        return new Promise((resolve, reject) => {
          if (!socketRef.current) {
            reject(new Error('Socket not connected'));
            return;
          }
          // An ack that never comes (the connection dropped mid-send, the gateway restarted) must
          // not leave the bubble "sending…" forever. After the timeout the caller treats the send
          // as failed; a late ack is ignored — retrying reuses `clientMsgId`, so it is idempotent.
          let settled = false;
          const ackTimer = setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error('Timed out waiting for the server to confirm the message'));
          }, SEND_ACK_TIMEOUT_MS);
          socketRef.current.emit(
            'message:send',
            {
              convId: payload.convId,
              clientMsgId: payload.clientMsgId,
              body: payload.body,
              kind: payload.kind ?? 'text',
              parentId: payload.parentId,
              meta: payload.meta,
              mentions: payload.mentions,
            },
            (response: {
              ok?: boolean;
              data?: {
                message?: {
                  id: string;
                  seq: string;
                  createdAt: string;
                  body?: string | null;
                  meta?: Record<string, unknown> | null;
                };
              };
              error?: { message?: string };
            }) => {
              if (settled) return;
              settled = true;
              clearTimeout(ackTimer);
              if (response?.ok && response.data?.message) {
                const confirmed = response.data.message;
                resolve({
                  id: confirmed.id,
                  seq: confirmed.seq,
                  createdAt: confirmed.createdAt || new Date().toISOString(),
                  body: confirmed.body,
                  meta: confirmed.meta,
                });
              } else {
                // Includes a malformed ack with neither `ok` nor `error`: never invent a
                // confirmation — the caller falls back to the idempotent REST send.
                reject(new Error(response?.error?.message || 'Failed to send message'));
              }
            },
          );
        });
      },
      markRead: (convId: string, seq: string | number) => {
        // Never rejects: a failed read receipt must not surface as an error in the UI.
        //
        // The socket is the fast path (it also fans the receipt out to other members and devices),
        // but read state must not DEPEND on it. With the gateway unreachable the socket object still
        // exists, so an emit is silently buffered by socket.io and its ack never fires — the badge
        // stayed lit forever. So: not connected, no ack in time, or a refused ack → the REST twin.
        // The server-side update is monotonic, so a late socket ack after the REST call is harmless.
        const wireSeq = seq.toString();
        const viaRest = async (): Promise<void> => {
          try {
            const res = await tsr.chat.markConversationRead.mutate({
              params: { id: convId },
              body: { seq: wireSeq },
            });
            if (res.status !== 200) console.warn('[socket] mark-read REST fallback refused', res.status);
          } catch {
            console.warn('[socket] mark-read REST fallback failed');
          }
        };
        const socket = socketRef.current;
        if (!socket || !socket.connected) return viaRest();
        return new Promise<boolean>((resolve) => {
          let settled = false;
          const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            resolve(false);
          }, READ_ACK_TIMEOUT_MS);
          socket.emit('message:read', { convId, seq: wireSeq }, (response?: SocketAck<unknown>) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(response?.ok === true);
          });
        }).then((acked) => (acked ? undefined : viaRest()));
      },
      onTyping: (listener) => {
        typingListenersRef.current.add(listener);
        return () => typingListenersRef.current.delete(listener);
      },
      startTyping: (convId: string) => {
        socketRef.current?.emit('typing:start', convId);
      },
      stopTyping: (convId: string) => {
        socketRef.current?.emit('typing:stop', convId);
      },
      editMessage: (payload) => {
        return new Promise((resolve, reject) => {
          if (!socketRef.current) {
            reject(new Error('Socket not connected'));
            return;
          }
          socketRef.current.emit(
            'message:edit',
            payload,
            (response: SocketAck<{ message: RealtimeMessage }>) => {
              if (response?.ok && response.data?.message) {
                resolve(response.data.message);
              } else {
                reject(new Error(response?.error?.message || 'Failed to edit message'));
              }
            },
          );
        });
      },
      deleteMessage: (payload) => {
        return new Promise((resolve, reject) => {
          if (!socketRef.current) {
            reject(new Error('Socket not connected'));
            return;
          }
          socketRef.current.emit(
            'message:delete',
            payload,
            (response: SocketAck<{ message: RealtimeMessage }>) => {
              if (response?.ok && response.data?.message) {
                resolve(response.data.message);
              } else {
                reject(new Error(response?.error?.message || 'Failed to delete message'));
              }
            },
          );
        });
      },
    }),
    [isConnected],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useNotificationSocket(): SocketContextValue {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useNotificationSocket() called outside <SocketProvider>');
  return ctx;
}

export function useChatSocket(): SocketContextValue {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useChatSocket() called outside <SocketProvider>');
  return ctx;
}
